/**
 * Narrowing a design-color -> thread mapping down to the per-design thread ceiling.
 *
 * The naive approach — walk entries weakest-first and reassign each one to its
 * nearest stronger neighbour's *current* thread — does not terminate. With five
 * entries A..E on distinct threads and a ceiling of 2, E adopts D's thread, then D
 * adopts C's; E is now alone on D's abandoned thread and the distinct count never
 * drops below 4. Merging whole clusters instead keeps every previously merged entry
 * attached to its target, so each step provably removes exactly one thread.
 */

import { hexToRgb, rgbDistanceSq } from './threads.js';

export interface PaletteEntry {
  /** Dominant color found in the design, '#rrggbb'. */
  hex: string;
  /** Fraction of opaque pixels this color covers, 0..1. */
  share: number;
  /** Nearest thread, pre-filled by the API and overridable by the user. */
  suggestedThreadId: string;
}

export interface ConstrainedMapping {
  mapping: Record<string, string>;
  /**
   * Palette hexes the constraint moved off their original thread. The modal marks
   * these rows so the seller can see which assignments were not their own.
   */
  mergedHexes: string[];
}

interface Cluster {
  threadId: string;
  hexes: string[];
  /** Combined share of every member — decides which cluster is absorbed next. */
  share: number;
  /** RGB of the highest-share member, used as the cluster's position in color space. */
  anchor: { r: number; g: number; b: number };
}

function buildClusters(
  palette: PaletteEntry[],
  mapping: Record<string, string>
): Cluster[] {
  const byThread = new Map<string, PaletteEntry[]>();

  for (const entry of palette) {
    const threadId = mapping[entry.hex] ?? entry.suggestedThreadId;
    const members = byThread.get(threadId);
    if (members) {
      members.push(entry);
    } else {
      byThread.set(threadId, [entry]);
    }
  }

  return Array.from(byThread.entries()).map(([threadId, members]) => {
    const strongest = members.reduce((a, b) => (b.share > a.share ? b : a));
    return {
      threadId,
      hexes: members.map((m) => m.hex),
      share: members.reduce((sum, m) => sum + m.share, 0),
      anchor: hexToRgb(strongest.hex),
    };
  });
}

/**
 * Fold the weakest clusters into their nearest neighbour until at most `maxColors`
 * distinct threads remain. Returns the resulting mapping plus the hexes that moved.
 */
export function constrainMappingToMaxColors(
  palette: PaletteEntry[],
  mapping: Record<string, string>,
  maxColors: number
): ConstrainedMapping {
  const result: Record<string, string> = {};
  for (const entry of palette) {
    result[entry.hex] = mapping[entry.hex] ?? entry.suggestedThreadId;
  }

  const ceiling = Math.max(1, maxColors);
  const original = { ...result };
  let clusters = buildClusters(palette, result);

  // Each pass removes exactly one cluster, so this cannot loop indefinitely.
  while (clusters.length > ceiling && clusters.length > 1) {
    const weakest = clusters.reduce((a, b) => (b.share < a.share ? b : a));

    let target: Cluster | null = null;
    let bestDistance = Infinity;
    for (const candidate of clusters) {
      if (candidate === weakest) continue;
      const distance = rgbDistanceSq(weakest.anchor, candidate.anchor);
      if (distance < bestDistance) {
        bestDistance = distance;
        target = candidate;
      }
    }
    if (!target) break;

    for (const hex of weakest.hexes) {
      result[hex] = target.threadId;
    }

    target.hexes = target.hexes.concat(weakest.hexes);
    target.share += weakest.share;
    clusters = clusters.filter((c) => c !== weakest);
  }

  const mergedHexes = palette
    .map((entry) => entry.hex)
    .filter((hex) => result[hex] !== original[hex]);

  return { mapping: result, mergedHexes };
}

/** Distinct threads a mapping uses — the number the counter shows against maxColors. */
export function countThreads(mapping: Record<string, string>): number {
  return new Set(Object.values(mapping)).size;
}
